import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Phone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sid = () => {
  let s = localStorage.getItem("cc_chat_sid");
  if (!s) { s = "cc-" + Math.random().toString(36).slice(2) + Date.now(); localStorage.setItem("cc_chat_sid", s); }
  return s;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi! I'm the Capital Corner AI concierge. Ask me about buying, selling, our listings, or the team \u2014 or I can connect you with Omer." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef(sid());

  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ behavior: "smooth" }); }, [msgs, open, loading]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId.current, message: content });
      setMsgs((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I hit a snag. Please call Omer at 443-955-8400." }]);
    } finally {
      setLoading(false);
    }
  };

  const quicks = ["I want to buy a home", "What's my home worth?", "Tell me about the team"];

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[85] w-14 h-14 rounded-full bg-[#b4936a] text-white flex items-center justify-center shadow-2xl hover:bg-[#a07f57] transition-all hover:scale-105"
        aria-label="Open chat"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      <div className={`fixed bottom-24 right-6 z-[85] w-[92vw] max-w-[390px] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 origin-bottom-right ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}`}>
        {/* header */}
        <div className="bg-[#0d0d0d] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <p className="font-serif-display text-lg leading-none">Capital Corner Concierge</p>
            <p className="text-[10px] tracking-luxe uppercase text-[#b4936a] mt-1">Assistant &middot; DMV Real Estate</p>
          </div>
          <a href="tel:443.955.8400" className="text-white/70 hover:text-[#b4936a] transition-colors" aria-label="Call Omer"><Phone size={18} /></a>
        </div>

        {/* messages */}
        <div className="h-[46vh] max-h-[420px] overflow-y-auto p-4 space-y-3 bg-[#f7f6f3]">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] px-4 py-2.5 text-[14px] leading-relaxed rounded-2xl ${m.role === "user" ? "bg-[#0d0d0d] text-white rounded-br-sm" : "bg-white text-neutral-800 border border-neutral-200 rounded-bl-sm"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start"><div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-sm px-4 py-2.5"><Loader2 size={16} className="animate-spin text-[#b4936a]" /></div></div>
          )}
          {msgs.length <= 1 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {quicks.map((q) => (
                <button key={q} onClick={() => send(q)} className="text-[12px] border border-[#b4936a]/40 text-[#8a6d45] px-3 py-1.5 rounded-full hover:bg-[#b4936a] hover:text-white transition-colors">{q}</button>
              ))}
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* input */}
        <div className="flex items-center gap-2 p-3 border-t border-neutral-200 bg-white">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask anything..."
            className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-[14px] outline-none focus:ring-1 focus:ring-[#b4936a]"
          />
          <button onClick={() => send()} disabled={loading} className="w-10 h-10 rounded-full bg-[#b4936a] text-white flex items-center justify-center hover:bg-[#a07f57] transition-colors disabled:opacity-50" aria-label="Send">
            <Send size={17} />
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
